import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SiteDataContext = createContext();

// Default fallback data (mirrors mock.js structure for backwards compatibility)
const defaultServices = {
  sv: [
    { id: 'hemstadning', title: 'Hemstädning', description: 'Regelbunden städning av ditt hem.', icon: 'Home', basePrice: 349, priceUnit: 'per tillfälle' },
    { id: 'flyttstadning', title: 'Flyttstädning', description: 'Grundlig städning vid flytt.', icon: 'Truck', basePrice: 45, priceUnit: 'per m²' },
    { id: 'storstadning', title: 'Storstädning', description: 'Djuprengöring av hela hemmet.', icon: 'Sparkles', basePrice: 55, priceUnit: 'per m²' },
    { id: 'fonsterputs', title: 'Fönsterputsning', description: 'Professionell rengöring av alla fönster.', icon: 'Square', basePrice: 75, priceUnit: 'per fönster' },
    { id: 'kontorsstadning', title: 'Kontorsstädning', description: 'Skräddarsydd städning för kontor och företag.', icon: 'Building2', basePrice: 35, priceUnit: 'per m²' },
    { id: 'trappstadning', title: 'Trappstädning', description: 'Regelbunden städning av trapphus.', icon: 'Stairs', basePrice: 299, priceUnit: 'per tillfälle' }
  ],
  en: [
    { id: 'hemstadning', title: 'Home Cleaning', description: 'Regular cleaning of your home.', icon: 'Home', basePrice: 349, priceUnit: 'per session' },
    { id: 'flyttstadning', title: 'Move-out Cleaning', description: 'Thorough cleaning when moving.', icon: 'Truck', basePrice: 45, priceUnit: 'per m²' },
    { id: 'storstadning', title: 'Deep Cleaning', description: 'Deep cleaning of the entire home.', icon: 'Sparkles', basePrice: 55, priceUnit: 'per m²' },
    { id: 'fonsterputs', title: 'Window Cleaning', description: 'Professional cleaning of all windows.', icon: 'Square', basePrice: 75, priceUnit: 'per window' },
    { id: 'kontorsstadning', title: 'Office Cleaning', description: 'Customized cleaning for offices.', icon: 'Building2', basePrice: 35, priceUnit: 'per m²' },
    { id: 'trappstadning', title: 'Stairwell Cleaning', description: 'Regular cleaning of stairwells.', icon: 'Stairs', basePrice: 299, priceUnit: 'per session' }
  ]
};

const defaultCompanyInfo = {
  name: 'Stockholm Elite Städ',
  address: 'Storängsgatan 9, 115 44 Stockholm',
  phone: '08 84 22 33',
  email: 'office@stockholmelite.se',
  social: {
    facebook: 'https://www.facebook.com/profile.php?id=100044283490397',
    instagram: 'https://www.instagram.com/sthlmelitestad/'
  }
};

const defaultCalculatorConfig = {
  propertyTypes: {
    sv: [
      { id: 'apartment', label: 'Lägenhet', multiplier: 1 },
      { id: 'house', label: 'Villa/Hus', multiplier: 1.2 },
      { id: 'office', label: 'Kontor', multiplier: 0.9 }
    ],
    en: [
      { id: 'apartment', label: 'Apartment', multiplier: 1 },
      { id: 'house', label: 'House/Villa', multiplier: 1.2 },
      { id: 'office', label: 'Office', multiplier: 0.9 }
    ]
  },
  cleaningTypes: {
    sv: [
      { id: 'regular', label: 'Hemstädning', pricePerSqm: 8 },
      { id: 'deep', label: 'Storstädning', pricePerSqm: 55 },
      { id: 'moving', label: 'Flyttstädning', pricePerSqm: 45 },
      { id: 'office', label: 'Kontorsstädning', pricePerSqm: 35 }
    ],
    en: [
      { id: 'regular', label: 'Regular Cleaning', pricePerSqm: 8 },
      { id: 'deep', label: 'Deep Cleaning', pricePerSqm: 55 },
      { id: 'moving', label: 'Move-out Cleaning', pricePerSqm: 45 },
      { id: 'office', label: 'Office Cleaning', pricePerSqm: 35 }
    ]
  },
  frequencies: {
    sv: [
      { id: 'once', label: 'Engångs', discount: 0 },
      { id: 'weekly', label: 'Varje vecka', discount: 20 },
      { id: 'biweekly', label: 'Varannan vecka', discount: 15 },
      { id: 'triweekly', label: 'Var 3:e vecka', discount: 10 },
      { id: 'monthly', label: 'Var 4:e vecka', discount: 5 }
    ],
    en: [
      { id: 'once', label: 'One-time', discount: 0 },
      { id: 'weekly', label: 'Weekly', discount: 20 },
      { id: 'biweekly', label: 'Every 2 weeks', discount: 15 },
      { id: 'triweekly', label: 'Every 3 weeks', discount: 10 },
      { id: 'monthly', label: 'Monthly', discount: 5 }
    ]
  },
  addons: {
    sv: [
      { id: 'windows', label: 'Fönsterputsning', price: 75, unit: 'per fönster' },
      { id: 'oven', label: 'Ugnsrengöring', price: 350, unit: 'styck' },
      { id: 'fridge', label: 'Kylskåpsrengöring', price: 250, unit: 'styck' },
      { id: 'balcony', label: 'Balkongstädning', price: 200, unit: 'styck' },
      { id: 'bedding', label: 'Byte av sängkläder', price: 50, unit: 'per säng' }
    ],
    en: [
      { id: 'windows', label: 'Window Cleaning', price: 75, unit: 'per window' },
      { id: 'oven', label: 'Oven Cleaning', price: 350, unit: 'each' },
      { id: 'fridge', label: 'Fridge Cleaning', price: 250, unit: 'each' },
      { id: 'balcony', label: 'Balcony Cleaning', price: 200, unit: 'each' },
      { id: 'bedding', label: 'Bed Linen Change', price: 50, unit: 'per bed' }
    ]
  },
  rutDeduction: 0.5,
  minPrice: 500
};

// Transform backend services data to frontend format
const transformServices = (backendServices) => {
  if (!backendServices || backendServices.length === 0) {
    return defaultServices;
  }
  
  const sv = backendServices.map(s => ({
    id: s.id,
    slug: s.slug || s.id,  // Use slug if available, fallback to id
    title: s.title_sv,
    description: s.description_sv,
    fullDescription: s.full_description_sv || '',
    icon: s.icon,
    basePrice: s.base_price,
    priceUnit: s.price_unit_sv,
    features: s.features || []
  }));
  
  const en = backendServices.map(s => ({
    id: s.id,
    slug: s.slug || s.id,  // Use slug if available, fallback to id
    title: s.title_en,
    description: s.description_en,
    fullDescription: s.full_description_en || '',
    icon: s.icon,
    basePrice: s.base_price,
    priceUnit: s.price_unit_en,
    features: s.features || []
  }));
  
  return { sv, en };
};

// Transform backend calculator config to frontend format
const transformCalculatorConfig = (backendConfig) => {
  if (!backendConfig) {
    return defaultCalculatorConfig;
  }
  
  return {
    propertyTypes: {
      sv: backendConfig.property_types.map(p => ({ id: p.id, label: p.label_sv, multiplier: p.multiplier })),
      en: backendConfig.property_types.map(p => ({ id: p.id, label: p.label_en, multiplier: p.multiplier }))
    },
    cleaningTypes: {
      sv: backendConfig.cleaning_types.map(c => ({ id: c.id, label: c.label_sv, pricePerSqm: c.price_per_sqm })),
      en: backendConfig.cleaning_types.map(c => ({ id: c.id, label: c.label_en, pricePerSqm: c.price_per_sqm }))
    },
    frequencies: {
      sv: backendConfig.frequencies.map(f => ({ id: f.id, label: f.label_sv, discount: f.discount })),
      en: backendConfig.frequencies.map(f => ({ id: f.id, label: f.label_en, discount: f.discount }))
    },
    addons: {
      sv: backendConfig.addons.map(a => ({ id: a.id, label: a.label_sv, price: a.price, unit: a.unit_sv })),
      en: backendConfig.addons.map(a => ({ id: a.id, label: a.label_en, price: a.price, unit: a.unit_en }))
    },
    rutDeduction: backendConfig.rut_deduction,
    minPrice: backendConfig.min_price
  };
};

// Transform backend company info to frontend format
const transformCompanyInfo = (backendInfo) => {
  if (!backendInfo) {
    return defaultCompanyInfo;
  }
  
  return {
    name: backendInfo.name,
    address: backendInfo.address,
    phone: backendInfo.phone,
    email: backendInfo.email,
    social: {
      facebook: backendInfo.facebook,
      instagram: backendInfo.instagram
    }
  };
};

export const SiteDataProvider = ({ children }) => {
  const [services, setServices] = useState(defaultServices);
  const [companyInfo, setCompanyInfo] = useState(defaultCompanyInfo);
  const [calculatorConfig, setCalculatorConfig] = useState(defaultCalculatorConfig);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSiteData = async () => {
      try {
        // Fetch all data in parallel
        const [servicesRes, configRes, companyRes] = await Promise.all([
          fetch(`${API_URL}/api/site/services`),
          fetch(`${API_URL}/api/site/calculator-config`),
          fetch(`${API_URL}/api/site/company-info`)
        ]);

        // Process services
        if (servicesRes.ok) {
          const servicesData = await servicesRes.json();
          setServices(transformServices(servicesData));
        }

        // Process calculator config
        if (configRes.ok) {
          const configData = await configRes.json();
          setCalculatorConfig(transformCalculatorConfig(configData));
        }

        // Process company info
        if (companyRes.ok) {
          const companyData = await companyRes.json();
          setCompanyInfo(transformCompanyInfo(companyData));
        }

      } catch (err) {
        console.error('Error fetching site data:', err);
        setError(err.message);
        // Keep using default values on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchSiteData();
  }, []);

  return (
    <SiteDataContext.Provider value={{
      services,
      companyInfo,
      calculatorConfig,
      isLoading,
      error
    }}>
      {children}
    </SiteDataContext.Provider>
  );
};

export const useSiteData = () => {
  const context = useContext(SiteDataContext);
  if (!context) {
    throw new Error('useSiteData must be used within a SiteDataProvider');
  }
  return context;
};

export default SiteDataProvider;
